import { useInstallation, useLinkSensorKit } from '@/installation/api'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, Link2, CheckCircle2 } from 'lucide-react'
import { useState } from 'react'
import { AxiosError } from 'axios'
import { StepFrame } from '@/components/installFlow/StepFrame'
import { StepHeader } from '@/components/installFlow/StepHeader'
import { StepFooter } from '@/components/installFlow/StepFooter'
import { useInstallFlowNavigation } from '@/installFlow/useInstallFlowNavigation'
import { useStepSearch } from '@/installFlow/useStepSearch'

export function LinkStep() {
  const search = useStepSearch('link')

  const locationId = search.locationId
  const sublocationId = search.sublocationId
  const installationId = search.installationId
  const sensorKitSerialNumber = search.installSensorKitSerialNumber
  const scanId = search.scanId

  const { goToStep, goToPreviousStep } = useInstallFlowNavigation()

  const { data: installation } = useInstallation(installationId)
  const linkSensorKit = useLinkSensorKit()

  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleLinkSensor = async () => {
    setErrorMessage(null)

    try {
      await linkSensorKit.mutateAsync({
        location_id: locationId,
        sublocation_id: sublocationId,
        installation_id: installationId,
        sensor_kit_serial_number: sensorKitSerialNumber,
        scan_id: scanId,
      })

      goToStep('health')
    } catch (error) {
      console.error('Failed to link sensor kit:', error)
      // Extract error message from axios error response
      if (error instanceof AxiosError && error.response?.data?.error) {
        setErrorMessage(error.response.data.error)
      } else {
        setErrorMessage('Failed to link sensor kit. Please try again.')
      }
    }
  }

  return (
    <StepFrame
      header={
        <StepHeader
          title="Link Sensor Kit"
          onBack={goToPreviousStep}
        />
      }
      main={
        <div className="space-y-6">
          <div className="text-center space-y-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-thalo-blueLight/10 flex items-center justify-center mb-4">
              <Link2 className="h-8 w-8 text-thalo-blue" />
            </div>
            <h2 className="text-xl font-semibold">Ready to link your sensor</h2>
            <p className="text-muted-foreground">
              Confirm the details below to link your Thalo sensor kit to this equipment
            </p>
          </div>

          <Card className="p-6 space-y-6">
            {/* Sensor Kit Details */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span>Sensor Kit</span>
              </div>
              <div className="pl-6">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-mono font-bold bg-gradient-to-r bg-clip-text text-transparent from-thalo-blue via-thalo-blueLight to-thalo-blue animate-text">
                    {sensorKitSerialNumber}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Serial number validated and ready to link
                </p>
              </div>
            </div>

            <div className="border-t" />

            {/* Installation Details */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span>Equipment</span>
              </div>
              <div className="pl-6">
                <div className="font-semibold">{installation.name}</div>
                {installation.asset && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {installation.asset.equipment_model?.manufacturer?.name} {installation.asset.equipment_model?.name}
                    {installation.asset.nameplate_capacity && installation.asset.nameplate_capacity_units && (
                      <span> • {installation.asset.nameplate_capacity} {installation.asset.nameplate_capacity_units}</span>
                    )}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  {installation.sublocation}
                </p>
              </div>
            </div>
          </Card>

          {errorMessage && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          <div className="text-center text-sm text-muted-foreground">
            <p>
              By linking this sensor, you'll start monitoring this equipment in real-time.
            </p>
          </div>
        </div>
      }
      footer={
        <StepFooter
          actions={[
            <Button
              key="link"
              onClick={handleLinkSensor}
              disabled={linkSensorKit.isPending}
              variant="thalo"
              className="w-full"
              size="lg"
            >
              {linkSensorKit.isPending ? 'Linking...' : 'Link Sensor Kit'}
            </Button>
          ]}
        />
      }
    />
  )
}
